/**
 * @fileoverview AR-670 — assigned-project branch of ProjectClassroomField:
 * loads the link state and renders the link CTA or the linked summary
 * (course link + "X of Y students matched" + unlink). POST /link runs the
 * roster matching synchronously (read-only backend), so the toaster comes
 * straight from the link response — no extra sync call.
 */

import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Anchor, Button, Group, Modal, Skeleton, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertTriangle, IconExternalLink, IconRefresh, IconUnlink } from '@tabler/icons-react'

import { notifications } from 'context'
import { isClassroomPortError, ProjectLinkResult, useProjectClassroomLink } from 'integrations/classroom'
import { LinkClassroomCourseModal } from './LinkClassroomCourseModal'

/** Backend 422s that deserve their own copy instead of the generic failure. */
const ERROR_MESSAGE_KEYS: Partial<Record<string, string>> = {
  already_linked: 'alreadyLinkedMessage',
  // 1 course = 1 project: the picked course belongs to ANOTHER project.
  course_already_linked: 'courseAlreadyLinkedToAnotherProject',
  not_linked: 'projectClassroomNotLinked',
  not_connected: 'projectClassroomConnectYourAccount',
  needs_reconnection: 'projectClassroomNeedsReconnection',
  // The tutor who linked disconnected — relinking, not connecting, fixes it.
  connection_gone: 'projectClassroomConnectionGone',
  course_not_found: 'projectClassroomCourseNotFound',
  // Google refused the call (account without Classroom, missing rights, quota).
  classroom_api_error: 'googleRefusedRequest',
}

interface ProjectClassroomFieldBodyProps {
  readonly projectId: string
  readonly header: ReactNode
}

export function ProjectClassroomFieldBody({ projectId, header }: ProjectClassroomFieldBodyProps) {
  const { t } = useTranslation()
  const link = useProjectClassroomLink(projectId)
  const [linkOpened, { open: openLink, close: closeLink }] = useDisclosure(false)
  const [unlinkOpened, { open: openUnlink, close: closeUnlink }] = useDisclosure(false)
  const tr = (key: string) => t(`classroom.${key}`)

  const notifyError = (error: unknown) => {
    if (isClassroomPortError(error)) {
      const messageKey = ERROR_MESSAGE_KEYS[error.code]
      if (messageKey) {
        notifications.show({ color: 'yellow', message: tr(messageKey) })
        return
      }
    }
    notifications.show({ color: 'red', message: tr('projectClassroomUpdateFailed') })
  }

  // Shared by link and re-run: roster is null only when the matching run
  // failed (the link is kept — rosterError says why).
  const notifyRosterResult = (result: ProjectLinkResult, title?: string) => {
    notifications.show({
      color: result.roster ? 'green' : 'yellow',
      title,
      message: result.roster
        ? t('classroom.classroomAssignRosterSummary', {
          matched: String(result.roster.matchedCount),
          unmatched: String(result.roster.unmatchedCount),
        })
        : tr('classroomAssignLinkedNoRoster'),
    })
  }

  const handleLink = async (courseId: string) => {
    notifyRosterResult(await link.linkCourse(courseId), tr('classroomAssignLinkedTitle'))
  }

  const handleSyncRoster = () => link.syncRoster().then(notifyRosterResult).catch(notifyError)

  const handleUnlink = async () => {
    closeUnlink()
    try {
      await link.unlink()
      notifications.show({
        color: 'green',
        title: tr('projectClassroomUnlinkedTitle'),
        message: tr('projectClassroomUnlinked'),
      })
    } catch (error) {
      notifyError(error)
    }
  }

  if (link.loading) {
    return (
      <Stack spacing={4} miw={160}>
        {header}
        <Skeleton height={26} width={150} radius='sm' />
      </Stack>
    )
  }

  const state = link.state
  // A hard failure loading the link state hides the field, like the old card.
  if (state === null) return null

  // Matched + unmatched partition the project students (backend contract), so
  // together they are the class total the prototype shows as "of Y".
  const rosterTotal = (state.rosterStudents?.length ?? 0) + (state.unmatchedStudents?.length ?? 0)

  return (
    <Stack spacing={4} miw={160} maw={300}>
      {header}

      {state.linked ? (
        <Stack spacing={2} align='flex-start'>
          {state.courseLink && (
            <Anchor href={state.courseLink} target='_blank' rel='noopener noreferrer' size='sm'>
              <Group spacing={4} noWrap>
                <Text size='sm' lineClamp={1} sx={{ maxWidth: 240 }}>
                  {state.courseName || tr('viewCourseInGoogleClassroom')}
                </Text>
                <IconExternalLink size={14} />
              </Group>
            </Anchor>
          )}
          <Text size='xs' color='dimmed'>
            {rosterTotal > 0
              ? t('classroom.projectClassroomStudentsSynced', {
                mapped: String(state.mappedStudentCount),
                total: String(rosterTotal),
              })
              : t('classroom.projectClassroomMappedStudents', { count: state.mappedStudentCount })}
          </Text>
          {/* Orphan link: the connection backing it is gone (its tutor
              disconnected) — the link survives but stopped syncing. The fix
              is the Unlink button right below + linking again. */}
          {state.connectionStatus === 'disconnected' && (
            <Alert color='yellow' variant='light' icon={<IconAlertTriangle size={14} />} p='xs'>
              <Text size='xs'>{tr('projectClassroomConnectionGone')}</Text>
            </Alert>
          )}
          <Group spacing='xs'>
            {/* Matching runs once at link time — this re-run maps students who
                joined the course later. Hidden on orphan links (no connection). */}
            {state.connectionStatus !== 'disconnected' && (
              <Button
                size='xs'
                variant='outline'
                leftIcon={<IconRefresh size={12} />}
                loading={link.busy === 'syncRoster'}
                disabled={link.busy !== null && link.busy !== 'syncRoster'}
                onClick={handleSyncRoster}
              >
                {tr('projectClassroomRerunMatching')}
              </Button>
            )}
            <Button
              size='xs'
              variant='outline'
              color='red'
              leftIcon={<IconUnlink size={12} />}
              loading={link.busy === 'unlink'}
              disabled={link.busy !== null && link.busy !== 'unlink'}
              onClick={openUnlink}
            >
              {tr('gcwUnlink')}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Button size='xs' variant='outline' onClick={openLink} sx={{ alignSelf: 'flex-start' }}>
          {tr('linkToGoogleClassroom')}
        </Button>
      )}

      <LinkClassroomCourseModal
        opened={linkOpened}
        onClose={closeLink}
        connectionStatus={state.connectionStatus}
        onLink={handleLink}
        onError={notifyError}
      />

      <Modal opened={unlinkOpened} onClose={closeUnlink} title={tr('projectClassroomUnlinkConfirmTitle')} size='sm'>
        <Stack spacing='md'>
          <Text size='sm'>{tr('projectClassroomUnlinkConfirmMessage')}</Text>
          <Group position='right'>
            <Button variant='default' onClick={closeUnlink}>
              {tr('cancel')}
            </Button>
            <Button color='red' onClick={handleUnlink} loading={link.busy === 'unlink'}>
              {tr('gcwUnlink')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
